import { z } from 'zod'
import { uuidSchema } from '../validators/uuid.validator.js'
import { clientStatusSchema } from '../enums/client-status.enum.js'
import { clientTypeSchema } from '../enums/client-type.enum.js'

/** Optional free-text field: trims, and treats an empty string as "not set". */
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((value) => (value === '' ? null : value))
    .nullish()

export const createClientInputSchema = z
  .object({
    name: z.string().trim().min(1, 'Name is required').max(200),
    type: clientTypeSchema,
    status: clientStatusSchema.optional(),

    addressLine1: optionalText(200),
    addressLine2: optionalText(200),
    city: optionalText(100),
    state: optionalText(100),
    postalCode: optionalText(20),

    notes: optionalText(5000),
    /**
     * Homes in the community, for an association. Proposals divide the
     * price by it ("$87 per home"), the way a board thinks about cost. Null
     * clears it; there is no sensible default to divide by.
     */
    homeCount: z
      .number()
      .int('Enter a whole number of homes')
      .positive('Enter at least one home')
      .max(100_000)
      .nullish(),
    ownerId: uuidSchema.nullish(),
  })
  .strict()

export type CreateClientInput = z.infer<typeof createClientInputSchema>

export const updateClientInputSchema = createClientInputSchema.partial().strict()

export type UpdateClientInput = z.infer<typeof updateClientInputSchema>

/**
 * Query params for the client list. `search` matches the client name;
 * `includeArchived` is opt-in so archived clients stay out of the way without
 * being unreachable.
 */
export const listClientsQuerySchema = z
  .object({
    search: z.string().trim().max(200).optional(),
    type: clientTypeSchema.optional(),
    status: clientStatusSchema.optional(),
    includeArchived: z
      .enum(['true', 'false'])
      .optional()
      .transform((value) => value === 'true'),
  })
  .strict()

export type ListClientsQuery = z.infer<typeof listClientsQuerySchema>
